import Pagination from "@/components/Pagination";
import PaginationSkeleton from "@/components/Pagination/PaginationSkeleton";
import { DEFAULT_PAGE_SIZE, PageInfo, PageSize, PaginationVariables } from "@/components/Pagination/Pagination";
import { TableFooter } from "@/components/Table";
import { gql } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "@/hooks/useSearchParams";

function paginationStateFromSearchParams(searchParams: URLSearchParams | null | undefined, pageSize: PageSize) {
  const before = searchParams?.get("before") ?? undefined;
  const after = searchParams?.get("after") ?? undefined;
  const currentPage = Number(searchParams?.get("currentPage")) || 0;

  const paginationVariables: PaginationVariables = {
    ...(!before && !after && { first: pageSize }),
    ...(before && { before, last: pageSize }),
    ...(after && { after, first: pageSize })
  };

  return { currentPage, paginationVariables };
}

const TABLE_PAGE_SIZE_KEY = "tablePageSize";

export const PAGE_INFO_FRAGMENT = gql`
  fragment PageInfo on ConnectionInterface {
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    totalCount
  }
`;

type EdgesResponse<T> = {
  edges: T[];
  nodes?: never;
};

type NodesResponse<T> = {
  edges?: never;
  nodes: T[];
};

type PaginatedResponse<T> = (NodesResponse<T> | EdgesResponse<T>) & {
  totalCount: number;
  pageInfo: PageInfo;
};

export type RenderFooter<T> = (paginatedResponse?: PaginatedResponse<T>, overrides?: Overrides) => () => JSX.Element;

interface Overrides {
  totalCount?: number;
  pageCount?: number;
  variant?: "compact" | "default";
  defaultPageSize?: PageSize;
  pageSize?: PageSize;
  loading?: boolean;
}

export interface BulkSelectionConfig {
  rowSelectionCount: number;
  onShowSelectedOnly?: () => void;
  isSelectedOnly?: boolean;
}

/**
 * Configuration for usePagination hook.
 *
 * @property defaultPageSize - Initial page size (defaults to 20)
 * @property bulkSelection - Optional bulk selection configuration for tables with row selection.
 *                           When provided, pass these values to TableFooter component props.
 *                           TableFooter will show/hide selection UI based on rowSelectionCount > 0.
 */
interface UsePaginationProps {
  defaultPageSize?: PageSize;
  bulkSelection?: BulkSelectionConfig;
}

/**
 * Hook for managing table pagination state and rendering.
 *
 * @returns {Object} Pagination utilities
 * @returns {PaginationVariables} paginationVariables - GraphQL cursor variables for queries
 * @returns {Function} createHeaderPagination - Creates <Pagination> element for TableControls (compact variant)
 * @returns {Function} createFooterPagination - Creates <Pagination> element for TableFooter (default variant)
 * @returns {Function} renderFooter - @deprecated Use createFooterPagination instead. Returns function for backward compatibility.
 * @returns {Function} renderPagination - @deprecated Use createHeaderPagination instead. Returns function for backward compatibility.
 * @returns {Function} resetPagination - Resets pagination state (call when filters change)
 * @returns {number} currentPage - Current page number (0-indexed)
 * @returns {PageSize} pageSize - Rows per page (20 | 50 | 100)
 *
 * @example
 * // NEW PATTERN (Preferred):
 * const { paginationVariables, createHeaderPagination, createFooterPagination } = usePagination();
 *
 * <Table
 *   renderHeader={() => (
 *     <TableControls pagination={createHeaderPagination(data?.items)}>
 *       <SearchInput />
 *     </TableControls>
 *   )}
 *   renderFooter={() => <TableFooter pagination={createFooterPagination(data?.items)} />}
 * />
 *
 * @example
 * // OLD PATTERN (Deprecated but still supported):
 * const { renderFooter, renderPagination } = usePagination();
 *
 * <Table renderFooter={renderFooter(data)} />
 *
 * @example
 * // With bulk selection
 * const { createFooterPagination } = usePagination({
 *   bulkSelection: {
 *     rowSelectionCount: selectedCount,
 *     onShowSelectedOnly: () => setShowOnly(!showOnly),
 *     isSelectedOnly: showOnly
 *   }
 * });
 */
function paginationVariablesMatch(a: PaginationVariables, b: PaginationVariables) {
  return a.first === b.first && a.last === b.last && a.after === b.after && a.before === b.before;
}

export default function usePagination({ defaultPageSize = DEFAULT_PAGE_SIZE, bulkSelection }: UsePaginationProps = {}) {
  const { searchParams, deleteSearchParams } = useParams();
  const beforeParam = searchParams?.get("before");
  const afterParam = searchParams?.get("after");
  const currentPageParam = searchParams?.get("currentPage");

  const getStoredPageSize = (): PageSize => {
    if (defaultPageSize !== DEFAULT_PAGE_SIZE) {
      return defaultPageSize;
    }

    try {
      const stored = localStorage.getItem(TABLE_PAGE_SIZE_KEY);
      if (stored) {
        const size = Number(stored) as PageSize;
        if ([20, 50, 100].includes(size)) {
          return size;
        }
      }
    } catch (e) {}

    return DEFAULT_PAGE_SIZE;
  };

  const [pageSize, setPageSizeState] = useState<PageSize>(getStoredPageSize);

  const setPageSize = useCallback((newSize: PageSize) => {
    setPageSizeState(newSize);
    try {
      localStorage.setItem(TABLE_PAGE_SIZE_KEY, String(newSize));
    } catch (e) {}
  }, []);

  const [currentPage, setCurrentPage] = useState(
    () => paginationStateFromSearchParams(searchParams, getStoredPageSize()).currentPage
  );
  const [paginationVariables, setPaginationVariables] = useState<PaginationVariables>(
    () => paginationStateFromSearchParams(searchParams, getStoredPageSize()).paginationVariables
  );

  useEffect(() => {
    const nextState = paginationStateFromSearchParams(searchParams, pageSize);
    setCurrentPage((prev) => (prev === nextState.currentPage ? prev : nextState.currentPage));
    setPaginationVariables((prev) =>
      paginationVariablesMatch(prev, nextState.paginationVariables) ? prev : nextState.paginationVariables
    );
  }, [beforeParam, afterParam, currentPageParam, pageSize, searchParams]);

  const resetPagination = useCallback(() => {
    setPaginationVariables({ first: pageSize });
    setCurrentPage(0);
    deleteSearchParams(["currentPage", "after", "before"]);
  }, [pageSize, deleteSearchParams]);

  const renderFooter = useCallback(
    function <T>(
      paginatedResponse?: PaginatedResponse<T>,
      { totalCount, pageCount, variant, loading }: Overrides = {}
    ) {
      return function Footer() {
        // Show skeleton during loading
        if (loading || !paginatedResponse) {
          return <PaginationSkeleton variant={variant} />;
        }

        const { nodes, edges, pageInfo, totalCount: responseTotalCount } = paginatedResponse;

        const pagination = (
          <Pagination
            pageInfo={pageInfo}
            setVariables={setPaginationVariables}
            variables={paginationVariables}
            setPageSize={setPageSize}
            pageSize={pageSize}
            pageRows={pageCount || (nodes || edges)?.length}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalRows={totalCount ?? responseTotalCount}
            variant={variant}
          />
        );

        if (variant === "compact") {
          return pagination;
        }

        return (
          <TableFooter
            pagination={pagination}
            rowSelectionCount={bulkSelection?.rowSelectionCount}
            onShowSelectedOnly={bulkSelection?.onShowSelectedOnly}
            isSelectedOnly={bulkSelection?.isSelectedOnly}
          />
        );
      };
    },
    [currentPage, pageSize, paginationVariables, bulkSelection, setPageSize]
  );

  const renderPagination = renderFooter;

  const createHeaderPagination = useCallback(
    function <T>(
      paginatedResponse?: PaginatedResponse<T>,
      { totalCount, pageCount, loading, pageSize: pageSizeOverride }: Overrides = {}
    ) {
      if (loading || !paginatedResponse) {
        return <PaginationSkeleton variant="compact" />;
      }

      const { nodes, edges, pageInfo, totalCount: responseTotalCount } = paginatedResponse;
      const resolvedPageSize = pageSizeOverride ?? pageSize;

      return (
        <Pagination
          pageInfo={pageInfo}
          setVariables={setPaginationVariables}
          variables={paginationVariables}
          setPageSize={setPageSize}
          pageSize={resolvedPageSize}
          pageRows={pageCount || (nodes || edges)?.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalRows={totalCount ?? responseTotalCount}
          variant="compact"
        />
      );
    },
    [currentPage, pageSize, paginationVariables, setPageSize]
  );

  const createFooterPagination = useCallback(
    function <T>(
      paginatedResponse?: PaginatedResponse<T>,
      { totalCount, pageCount, loading, pageSize: pageSizeOverride }: Overrides = {}
    ) {
      if (loading || !paginatedResponse) {
        return <PaginationSkeleton variant="default" />;
      }

      const { nodes, edges, pageInfo, totalCount: responseTotalCount } = paginatedResponse;
      const resolvedPageSize = pageSizeOverride ?? pageSize;

      return (
        <Pagination
          pageInfo={pageInfo}
          setVariables={setPaginationVariables}
          variables={paginationVariables}
          setPageSize={setPageSize}
          pageSize={resolvedPageSize}
          pageRows={pageCount || (nodes || edges)?.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalRows={totalCount ?? responseTotalCount}
          variant="default"
        />
      );
    },
    [currentPage, pageSize, paginationVariables, setPageSize]
  );

  return {
    paginationVariables,
    createHeaderPagination,
    createFooterPagination,
    // @deprecated - Use createFooterPagination with <TableFooter> instead
    renderFooter,
    // @deprecated - Use createHeaderPagination instead
    renderPagination,
    resetPagination,
    currentPage,
    pageSize
  };
}
